import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useScreen } from '../../../hooks'
import { toCSSPixel } from '../../../utils/styles'
import { PageLoader } from '../../Page'
import {
  usePdfViewerNavigation,
  usePdfViewerSelectionAreaBox,
} from '../../PdfViewer'
import { useWidgetListPipeline } from '../../Widget'
import {
  useDocFlowContext,
  useDocFlowMappingFileProcess,
  useDocFlowMappingHeaderActions,
  useDocFlowMappping,
  useDocFlowPdfSelectArea,
  useDocFlowSelectAreaInfo,
  useGetUploadingFiles,
} from '../DocFlow.hooks'
import { DocFlowMappingProvider } from '../DocFlowMappingProvider'
import {
  DocFlowMappingWidgets,
  getDocFlowWidgets,
  MappingWidgetType,
} from '../DocFlowMappingWidgets'

import { DocFlowMappingFileListSlider } from './DocFlowMappingFileListSlider'
import { DocFlowMappingPreviewContent } from './DocFlowMappingPreviewContent'

import * as styles from './DocFlowMapping.module.css'

function DocFlowMapping() {
  const { t } = useTranslation()

  const { containerRef } = useDocFlowContext()

  const { isMobileScreenSize } = useScreen()

  const [isMappedDataLoading, setMappedDataLoading] = useState(false)

  const widgets = getDocFlowWidgets(t)

  const {
    handleReloadUploadingFilesAll,
    isInitiated,
    uploadingFiles: files,
  } = useGetUploadingFiles()

  const {
    activeWidget,
    handleClickNext,
    handleClickPrevious,
    setActiveWidget,
  } = useWidgetListPipeline({
    initialActive: MappingWidgetType.Type,
    widgets,
  })

  const {
    handleHideSelectedArea,
    hideSelectArea,
    initSelectBoxStyle,
    isChanged,
    isChanging,
    isInitSelectArea,
    isPdfFinishedRendering,
    pageNumber,
    pdfPageCoordinates,
    pdfPageRef,
    pdfViewerInstance,
    selectAreaBoxProps,
    selectedBox,
    setChanged,
    setDefaultParams: setPdfViewerSelectAreaInitialParams,
    setSelectedBox,
    showSelectArea,
    viewerProps,
  } = usePdfViewerSelectionAreaBox()

  const {
    handleGetSelectedAreaInfo,
    handleResetCurrentSelectedArea,
    isOpenValueInfo,
    isPdfSelectAreaInfoValueLoading,
    pdfSelectAreaInfoValue,
    setOpenValueInfo: handleToggleOpenSelectAreaInfo,
    setPdfSelectAreaInfoValue,
  } = useDocFlowSelectAreaInfo({
    isInitSelectArea,
    pdfPageCoordinates,
    selectedBox,
    setSelectedBox,
    onHideSelectedArea: handleHideSelectedArea,
  })

  const {
    getFileProcess,
    handleClickResetButton,
    handleUpdateFileProcess,
    isProcessDataLoading,
    processData,
    setProcessData,
  } = useDocFlowMappingFileProcess({
    setActiveWidget,
    onReloadUploadingFilesAll: handleReloadUploadingFilesAll,
    onResetCurrentSelectedArea: handleResetCurrentSelectedArea,
  })

  const {
    handleInitSelectArea,
    handleToggleSelectArea,
    selectBoxAreaDisabled,
    selectBoxAreaFloatingProps,
  } = useDocFlowPdfSelectArea({
    activeWidget,
    handleToggleOpenSelectAreaInfo,
    hideSelectArea,
    initSelectBoxStyle,
    isChanged,
    isInitSelectArea,
    isOpenValueInfo,
    isPdfFinishedRendering,
    pageNumber,
    pdfPageCoordinates,
    pdfPageRef,
    pdfSelectAreaInfoValue,
    pdfViewerInstance,
    processData,
    selectedBox,
    showSelectArea,
  })

  const { arrowNavigationY } = usePdfViewerNavigation({
    containerRef,
    isPdfFinishedRendering,
    pageNumber,
    pdfPageRef,
    viewerRootElement: viewerProps.viewerRootRef.current,
  })

  const {
    activeFileBytes,
    activeFileForPreview,
    handleChangeActiveFile,
    handleGetActiveFile,
    isPageLoader,
    loadingFileBytes,
    setPageLoader,
    someFileInProgress,
  } = useDocFlowMappping({
    files,
    handleToggleOpenSelectAreaInfo,
    setPdfViewerSelectAreaInitialParams,
    setProcessData,
  })

  useEffect(() => {
    // reset pdf selection area on layout change
    setPdfViewerSelectAreaInitialParams()
  }, [isMobileScreenSize])

  const { nextButtonProps, nextPhaseDisabled } = useDocFlowMappingHeaderActions(
    { files, isInitiated }
  )

  const activeWidgetMobileContent = (
    <DocFlowMappingPreviewContent
      activeFileForPreview={activeFileForPreview}
      activeWidget={activeWidget}
      processData={processData}
      viewerProps={viewerProps}
    />
  )

  return (
    <div
      className={styles.DocFlowMapping}
      style={{
        '--doc-flow-pdf-viewer-arrow-navigation-y':
          toCSSPixel(arrowNavigationY),
      }}
    >
      {isPageLoader ? <PageLoader /> : null}

      <DocFlowMappingProvider
        activeFile={activeFileForPreview}
        activeFileBytes={activeFileBytes}
        activeWidget={activeWidget}
        files={files}
        handleClickNextWidget={handleClickNext}
        handleClickPreviousWidget={handleClickPrevious}
        handleGetActiveFile={handleGetActiveFile}
        handleGetSelectedAreaInfo={handleGetSelectedAreaInfo}
        handleHideSelectedArea={handleHideSelectedArea}
        handleInitSelectArea={handleInitSelectArea}
        handleReloadFileProcess={getFileProcess}
        handleReloadUploadingFilesAll={handleReloadUploadingFilesAll}
        handleResetCurrentSelectedArea={handleResetCurrentSelectedArea}
        handleResetProcess={handleClickResetButton}
        handleToggleOpenSelectAreaInfo={handleToggleOpenSelectAreaInfo}
        handleToggleSelectArea={handleToggleSelectArea}
        handleUpdateFileProcess={handleUpdateFileProcess}
        hideSelectArea={hideSelectArea}
        isChanged={isChanged}
        isChanging={isChanging}
        isFileListInitiated={isInitiated}
        isInitSelectArea={isInitSelectArea.current}
        isMappedDataLoading={isMappedDataLoading}
        isOpenSelectAreaInfo={isOpenValueInfo}
        isPdfFinishedRendering={isPdfFinishedRendering}
        isPdfSelectAreaInfoValueLoading={isPdfSelectAreaInfoValueLoading}
        isProcessDataLoading={isProcessDataLoading}
        loadingFileBytes={loadingFileBytes}
        nextButtonProps={nextButtonProps}
        nextPhaseDisabled={nextPhaseDisabled}
        pageNumber={pageNumber}
        pdfPageCoordinates={pdfPageCoordinates}
        pdfPageRef={pdfPageRef}
        pdfViewerInstance={pdfViewerInstance}
        processData={processData}
        selectAreaBoxProps={{
          ...selectAreaBoxProps,
          ...selectBoxAreaFloatingProps,
          infoItems: pdfSelectAreaInfoValue,
          selectBoxAreaDisabled,
        }}
        selectedBox={selectedBox}
        setActiveWidget={setActiveWidget}
        setChanged={setChanged}
        setMappedDataLoading={setMappedDataLoading}
        setPageLoader={setPageLoader}
        setPdfSelectAreaInfoValue={setPdfSelectAreaInfoValue}
        setPdfViewerSelectAreaInitialParams={
          setPdfViewerSelectAreaInitialParams
        }
        setProcessData={setProcessData}
        setSelectedBox={setSelectedBox}
        showSelectArea={showSelectArea}
        someFileInProgress={someFileInProgress}
        widgets={widgets}
      >
        <DocFlowMappingFileListSlider
          onChangeActiveFile={handleChangeActiveFile}
        />

        <div className={styles.previewContent}>
          {!isMobileScreenSize ? (
            <DocFlowMappingPreviewContent
              activeFileForPreview={activeFileForPreview}
              activeWidget={activeWidget}
              processData={processData}
              viewerProps={viewerProps}
            />
          ) : null}
          <DocFlowMappingWidgets
            activeWidgetMobileContent={activeWidgetMobileContent}
          />
        </div>
      </DocFlowMappingProvider>
    </div>
  )
}

export default DocFlowMapping
